import React, { useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { useQuery } from '@apollo/client';
import { Box, Flex } from 'rebass';
import useNotify from '../../../../hooks/useNotify';
import useSessionInfo from '../../../../hooks/useSessionInfo';
import ThirdPaneLayout, {
  MainColumn,
} from '../../../../components/Layout/ThirdPaneLayout';
import ThirdPane from '../../../../components/ThirdPane';
import ThirdPaneHeader from '../../../../components/ThirdPane/ThirdPaneHeader';
import ThirdPaneContent from '../../../../components/ThirdPane/ThirdPaneContent';
import ThirdPaneFooter from '../../../../components/ThirdPane/ThirdPaneFooter';
import SurveyTemplateForm, {
  SurveyTemplateInput,
} from '../../../../components/WaterHazard/SurveyTemplateForm';
import HazardSurveyResult from '../../../../components/WaterHazard/HazardSurveyResult';
import {
  GET_SURVEY_TEMPLATES,
  SurveyTemplateItem,
} from './SurveyTemplates.query';
import { GET_WATER_HAZARDS } from '../waterHazards/WaterHazards.query';

type SurveyTemplateDetailProps = RouteComponentProps<{ id: string }>;

function SurveyTemplateDetail({
  match,
  history,
}: SurveyTemplateDetailProps): JSX.Element {
  const templateId = match.params.id;
  const { showError } = useNotify();
  const { session } = useSessionInfo();
  const companyProfileId = session?.companyProfile?.id;

  const [template, setTemplate] = useState<SurveyTemplateInput>({
    name: '',
    description: '',
    instructions: '',
    otherHazardsQuestion: false,
    protectedQuestion: false,
    hazardIds: [],
  });

  const { data: existingData, loading: itemLoading } = useQuery(
    GET_SURVEY_TEMPLATES,
    {
      variables: {
        where: { id: templateId },
      },
    }
  );

  useEffect(() => {
    if (existingData?.waterHazardSurveyTemplates?.[0]) {
      const existing = existingData
        .waterHazardSurveyTemplates[0] as SurveyTemplateItem;
      // If this template is owned by the current water supplier, go to edit
      if (
        companyProfileId &&
        existing.waterSupplierId === companyProfileId
      ) {
        history.replace(`/water-supplier/survey-templates/${templateId}/edit`);
        return;
      }
      setTemplate({
        name: existing.name,
        description: existing.description,
        instructions: existing.instructions || '',
        otherHazardsQuestion: !!existing.otherHazardsQuestion,
        protectedQuestion: !!existing.protectedQuestion,
        hazardIds: existing.items.map((i) => i.waterHazardId),
      });
    }
  }, [existingData, companyProfileId]);

  const { data: hazardsData, loading: hazardsLoading } =
    useQuery(GET_WATER_HAZARDS);

  return (
    <ThirdPaneLayout paneVisible={true}>
      <MainColumn width="calc(100% - 600px)">
        <Flex flexDirection="column" height="100%">
          <Box p={3} bg="gray.100" flexGrow={1} sx={{ overflowY: 'auto' }}>
            <HazardSurveyResult
              template={template}
              hazards={hazardsData?.waterHazards ?? []}
              mode="preview"
            />
          </Box>
        </Flex>
      </MainColumn>
      <ThirdPane width={600} visible={true}>
        <ThirdPaneHeader title="Survey Template Details" />
        <ThirdPaneContent p={0}>
          {itemLoading ? (
            <Flex justifyContent="center" alignItems="center" flexGrow={1}>
              Loading...
            </Flex>
          ) : (
            <SurveyTemplateForm
              template={template}
              onChange={() => {}}
              readOnly={true}
            />
          )}
        </ThirdPaneContent>
        <ThirdPaneFooter />
      </ThirdPane>
    </ThirdPaneLayout>
  );
}

export default withRouter(SurveyTemplateDetail);
